import React, { useEffect, useState } from "react";
import { Building2, ChevronRight } from "lucide-react";
import { companiesApi } from "../../../lib/api";

/** Collapsible "Companies with access" panel with a live filter. Only shows
 *  ACTIVE companies (server-side filter). */
export const CompaniesAccessSection = ({ facilityId }) => {
  const [state, setState] = useState({ loading: true, items: [] });
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    companiesApi.byFacility(facilityId)
      .then((r) => { if (!cancelled) setState({ loading: false, items: r.items || [] }); })
      .catch(() => { if (!cancelled) setState({ loading: false, items: [] }); });
    return () => { cancelled = true; };
  }, [facilityId]);

  if (state.loading) return null;
  const count = state.items.length;
  const term = q.trim().toLowerCase();
  const filtered = term
    ? state.items.filter((c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.company_id || "").toLowerCase().includes(term))
    : state.items;

  return (
    <div data-testid="companies-access-section">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100"
      >
        <div className="flex items-center gap-2">
          <Building2 size={14} strokeWidth={2.5} className="text-slate-700" />
          <span className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
            Companies with access · {count}
          </span>
        </div>
        <ChevronRight size={16} className={`transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="mt-2 rounded-md border border-slate-200">
          <div className="p-2 border-b border-slate-100">
            <input
              data-testid="companies-access-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company name or ID…"
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-nmi-600"
            />
            {term && (
              <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
                <span>{filtered.length} of {count} match "{q}"</span>
                <button onClick={() => setQ("")} className="text-nmi-700 hover:text-nmi-800 font-semibold">Clear</button>
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {count === 0 && <div className="p-3 text-xs italic text-slate-500">No companies list this facility yet.</div>}
            {count > 0 && filtered.length === 0 && (
              <div className="p-3 text-xs italic text-slate-500">No matches for "{q}"</div>
            )}
            {filtered.slice(0, 100).map((c) => (
              <div key={c.company_id} className="flex items-start justify-between gap-2 px-3 py-2 text-xs">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-900">{c.name}</div>
                  <div className="font-mono text-[10px] text-slate-500">{c.company_id}</div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  {c.via === "extra" && (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">Extra</span>
                  )}
                  {(c.plans || []).map((p) => (
                    <span key={p} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-slate-700">{p}</span>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length > 100 && <div className="p-2 text-center text-[11px] italic text-slate-500">…and {filtered.length - 100} more (refine your search)</div>}
          </div>
        </div>
      )}
    </div>
  );
};
